import React, { useState, useEffect } from 'react';
import { Input, Textarea, Select, SelectItem, Autocomplete, AutocompleteItem } from '@heroui/react';
import { Plus, Loader2, Monitor, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { Button } from '@/shared/ui/components/Button';
import { Modal, ConfirmModal } from '@/shared/ui';
import { cn, formatDate } from '@/shared/lib/utils';
import { logger } from '@/shared/lib';
import type { Phoneboard } from '@/shared/lib/types';
import { usePhoneboardSearch, useGroupsSearch, useUpdatePhoneboard, useCreatePhoneboard, useDeletePhoneboard } from '@/entities/phoneboard';

interface PhoneboardModalData {
  name: string;
  model: string;
  totalPhones: number;
  location: string;
  ipAddress: string;
  description: string;
  groups: string[];
  serialNumber: string;
}

const statusColors = {
  NORMAL: 'text-success bg-success/10',
  PARTIAL: 'text-warning bg-warning/10',
  CRITICAL: 'text-danger bg-danger/10',
} as const;

const statusLabels = {
  NORMAL: '정상',
  PARTIAL: '일부비상',
  CRITICAL: '비상',
} as const;

export const PhoneboardManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPhoneboard, setSelectedPhoneboard] = useState<Phoneboard | null>(null);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [modalData, setModalData] = useState<PhoneboardModalData>({
    name: '',
    model: '',
    totalPhones: 20,
    location: '',
    ipAddress: '',
    description: '',
    groups: [],
    serialNumber: '',
  });

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on search
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Build search parameters
  const searchParams = {
    phoneboardName: debouncedSearchTerm || undefined,
    status: selectedStatus !== 'all' ? selectedStatus : undefined,
    page: currentPage - 1,
    size: pageSize,
  };

  // Fetch phoneboard status summaries with search
  const { data: statusSummariesResponse, isLoading } = usePhoneboardSearch(searchParams);

  // Fetch groups for autocomplete
  const { data: groupsResponse } = useGroupsSearch(
    { name: groupSearchTerm || undefined },
    true
  );

  // Extract data from API response
  const phoneboardSummaries = statusSummariesResponse?.data?.content || [];
  const totalElements = statusSummariesResponse?.data?.total_elements || 0;
  const totalPages = statusSummariesResponse?.data?.total_pages || 0;
  const availableGroups = groupsResponse?.data?.content || [];

  // Phoneboard mutations
  const createPhoneboardMutation = useCreatePhoneboard();
  const updatePhoneboardMutation = useUpdatePhoneboard();
  const deletePhoneboardMutation = useDeletePhoneboard();

  const handleAddPhoneboard = () => {
    setModalData({
      name: '',
      model: '',
      totalPhones: 20,
      location: '',
      ipAddress: '',
      description: '',
      groups: [],
      serialNumber: '',
    });
    setShowAddModal(true);
  };

  const handleEditPhoneboard = (phoneboard: typeof phoneboardSummaries[0]) => {
    setSelectedPhoneboard({
      id: phoneboard.id,
      name: phoneboard.phoneboard_name || '',
      status: phoneboard.status.toLowerCase() as 'normal' | 'partial_error' | 'error',
      connectedPhones: phoneboard.connection_status.connected_count,
      totalPhones: phoneboard.connection_status.total_count,
      phones: [],
      lastUpdated: phoneboard.updated_at,
    });
    setModalData({
      name: phoneboard.phoneboard_name || '',
      model: '',
      totalPhones: phoneboard.connection_status.total_count,
      location: '',
      ipAddress: '',
      description: '',
      groups: phoneboard.groups || [],
      serialNumber: phoneboard.serial_number || '',
    });
    setShowEditModal(true);
  };

  const handleDeletePhoneboard = (phoneboard: Phoneboard) => {
    setSelectedPhoneboard(phoneboard);
    setShowDeleteModal(true);
  };

  const handleModalSubmit = async () => {
    // 현재 입력 중인 그룹명이 있으면 그룹 목록에 추가
    let finalGroups = [...modalData.groups];
    if (groupSearchTerm.trim() && !finalGroups.includes(groupSearchTerm.trim())) {
      finalGroups = [groupSearchTerm.trim()];
    }

    if (!selectedPhoneboard?.id) {
      // 추가 모드
      try {
        await createPhoneboardMutation.mutateAsync({
          body: {
            serial_number: modalData.serialNumber,
            phoneboard_name: modalData.name,
            groups: finalGroups,
            description: modalData.description,
          },
        });
        setShowAddModal(false);
        setGroupSearchTerm('');
        // 폼 초기화
        setModalData({
          name: '',
          model: '',
          totalPhones: 20,
          location: '',
          ipAddress: '',
          description: '',
          groups: [],
          serialNumber: '',
        });
      } catch (error) {
        logger.error('Failed to create phoneboard:', error);
      }
      return;
    }

    // 수정 모드
    try {
      await updatePhoneboardMutation.mutateAsync({
        path: { id: selectedPhoneboard.id },
        body: {
          phoneboard_name: modalData.name,
          groups: finalGroups,
          description: modalData.description,
        },
      });
      setShowEditModal(false);
      setGroupSearchTerm('');
    } catch (error) {
      logger.error('Failed to update phoneboard:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPhoneboard?.id) {
      return;
    }

    try {
      await deletePhoneboardMutation.mutateAsync({
        path: { id: selectedPhoneboard.id },
      });
      setShowDeleteModal(false);
      setSelectedPhoneboard(null);
    } catch (error) {
      logger.error('Failed to delete phoneboard:', error);
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleStatusChange = (newStatus: string) => {
    setSelectedStatus(newStatus);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center">
            폰보드 관리
          </h2>
          <p className="text-gray-500 text-sm mt-1">폰보드 장비와 연결된 디바이스를 관리합니다</p>
        </div>

        <div className="flex space-x-3">
          <Button size="sm" onClick={handleAddPhoneboard}>
            <Plus className="w-4 h-4 mr-1" /> 폰보드 추가
          </Button>
        </div>
      </div>

      {/* 본문 */}
      <div>
        {/* 검색 및 필터 */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-grow min-w-[250px]">
            <Input
              type="text"
              placeholder="폰보드명, 모델, 위치, IP로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              classNames={{
                input: "bg-white",
                inputWrapper: "bg-white border border-gray-300"
              }}
            />
          </div>
          <div className="flex space-x-3">
            <Select
              selectedKeys={[selectedStatus]}
              onSelectionChange={(keys) => handleStatusChange(Array.from(keys)[0] as string)}
              classNames={{
                trigger: "bg-white border border-gray-300"
              }}
              className="w-40"
              aria-label="상태 필터"
            >
              <SelectItem key="all">모든 상태</SelectItem>
              <SelectItem key="NORMAL">정상</SelectItem>
              <SelectItem key="PARTIAL">일부비상</SelectItem>
              <SelectItem key="CRITICAL">비상</SelectItem>
            </Select>
          </div>
        </div>

        {/* 폰보드 테이블 */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                로딩 중...
              </div>
            </div>
          ) : phoneboardSummaries.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">
                {debouncedSearchTerm || selectedStatus !== 'all'
                  ? '검색 결과가 없습니다'
                  : '데이터가 없습니다'}
              </div>
            </div>
          ) : (
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">폰보드명</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">그룹</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">연결상태</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">마지막 업데이트</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {phoneboardSummaries.map((phoneboard) => (
                  <tr key={phoneboard.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <Monitor className="w-4 h-4 text-primary mr-2" />
                        <div>
                          <div className="text-sm font-medium">{phoneboard.phoneboard_name || 'N/A'}</div>
                          <div className="text-xs text-gray-500">ID: {phoneboard.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {phoneboard.groups && phoneboard.groups.length > 0 ? (
                          phoneboard.groups.map((group, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                            >
                              {group}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(phoneboard.connection_status.total_count, 10) }, (_, i) => (
                            <div
                              key={i}
                              className={cn(
                                'w-2 h-2 rounded-full',
                                i < phoneboard.connection_status.connected_count
                                  ? 'bg-success'
                                  : 'bg-gray-300'
                              )}
                            ></div>
                          ))}
                          {phoneboard.connection_status.total_count > 10 && (
                            <span className="text-xs text-gray-500 ml-1">...</span>
                          )}
                        </div>
                        <span className="text-sm">
                          {phoneboard.connection_status.connected_count}/{phoneboard.connection_status.total_count}
                          <span className="text-xs text-gray-500 ml-1">
                            ({phoneboard.connection_status.connection_percentage}%)
                          </span>
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={cn(
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        statusColors[phoneboard.status as keyof typeof statusColors] || 'text-gray-500 bg-gray-100'
                      )}>
                        <span className={cn('w-2 h-2 rounded-full mr-1',
                          phoneboard.status === 'NORMAL' ? 'bg-success' :
                          phoneboard.status === 'PARTIAL' ? 'bg-warning' : 'bg-danger'
                        )}></span>
                        {statusLabels[phoneboard.status as keyof typeof statusLabels] || phoneboard.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">{formatDate(phoneboard.updated_at)}</td>
                    <td className="py-3 px-4 text-sm">
                      <button
                        onClick={() => handleEditPhoneboard(phoneboard)}
                        className="text-primary hover:text-primary/80 mr-3"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDeletePhoneboard({
                          id: phoneboard.id,
                          name: phoneboard.phoneboard_name || '',
                          status: phoneboard.status.toLowerCase() as 'normal' | 'partial_error' | 'error',
                          connectedPhones: phoneboard.connection_status.connected_count,
                          totalPhones: phoneboard.connection_status.total_count,
                          phones: [],
                          lastUpdated: phoneboard.updated_at,
                        })}
                        className="text-danger hover:text-danger/80"
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 페이지네이션 */}
        {!isLoading && phoneboardSummaries.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
            {/* Page info and size selector */}
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                전체 <span className="font-medium">{totalElements}</span>개 중{' '}
                <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span>-
                <span className="font-medium">{Math.min(currentPage * pageSize, totalElements)}</span> 표시
              </div>

              <Select
                selectedKeys={[String(pageSize)]}
                onSelectionChange={(keys) => handlePageSizeChange(Number(Array.from(keys)[0]))}
                classNames={{
                  trigger: "bg-white border border-gray-300 h-8 min-h-8",
                  value: "text-sm"
                }}
                className="w-28"
                aria-label="페이지 크기 선택"
              >
                <SelectItem key="5">5개씩</SelectItem>
                <SelectItem key="10">10개씩</SelectItem>
                <SelectItem key="20">20개씩</SelectItem>
                <SelectItem key="50">50개씩</SelectItem>
              </Select>
            </div>

            {/* Page navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  // Show current page and 2 pages before/after
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage < 4) {
                    pageNum = i + 1;
                  } else if (currentPage > totalPages - 3) {
                    pageNum = totalPages - 5 + i + 1;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={cn(
                        'min-w-[32px] h-8 px-2 text-sm rounded transition-colors',
                        currentPage === pageNum
                          ? 'bg-primary text-white font-medium'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      )}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage >= totalPages}
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 폰보드 추가 모달 */}
      <Modal isOpen={showAddModal} onClose={() => {
        setShowAddModal(false);
        setGroupSearchTerm('');
      }} size="default">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">새 폰보드 추가</h3>
          <form className="space-y-4">
            <div>
              <Input
                type="text"
                label="폰보드명"
                value={modalData.name}
                onChange={(e) => setModalData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="폰보드명을 입력하세요"
              />
            </div>
            <div>
              <Input
                type="text"
                label="시리얼 번호"
                value={modalData.serialNumber}
                onChange={(e) => setModalData(prev => ({ ...prev, serialNumber: e.target.value }))}
                placeholder="시리얼 번호를 입력하세요"
                isRequired
              />
            </div>
            <div>
              <Autocomplete
                label="그룹"
                placeholder="그룹을 검색하고 선택하거나 새 그룹명을 입력하세요"
                inputValue={groupSearchTerm}
                onInputChange={(value) => setGroupSearchTerm(value)}
                selectedKey={modalData.groups[0] || null}
                onSelectionChange={(key) => {
                  if (key) {
                    setModalData(prev => ({ ...prev, groups: [key as string] }));
                  } else {
                    setModalData(prev => ({ ...prev, groups: [] }));
                  }
                }}
                allowsCustomValue
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && groupSearchTerm.trim()) {
                    e.preventDefault();
                    setModalData(prev => ({ ...prev, groups: [groupSearchTerm.trim()] }));
                    setGroupSearchTerm('');
                  }
                }}
                className="max-w-full"
              >
                {availableGroups.map((group: any) => (
                  <AutocompleteItem key={group.name}>
                    {group.name}
                  </AutocompleteItem>
                ))}
              </Autocomplete>
            </div>
            <div>
              <Textarea
                label="설명"
                value={modalData.description}
                onChange={(e) => setModalData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="폰보드 용도나 특이사항을 입력하세요"
                minRows={3}
              />
            </div>
          </form>
          <div className="flex justify-end space-x-3 mt-6">
            <Button type="button" variant="outline" onClick={() => {
              setShowAddModal(false);
              setGroupSearchTerm('');
            }}>
              취소
            </Button>
            <Button type="button" onClick={handleModalSubmit}>
              추가
            </Button>
          </div>
        </div>
      </Modal>

      {/* 폰보드 수정 모달 */}
      <Modal isOpen={showEditModal} onClose={() => {
        setShowEditModal(false);
        setGroupSearchTerm('');
      }} size="default">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">폰보드 수정</h3>
          <form className="space-y-4">
            <div>
              <Input
                type="text"
                label="폰보드명"
                value={modalData.name}
                onChange={(e) => setModalData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <Input
                type="text"
                label="시리얼 번호"
                value={modalData.serialNumber}
                onChange={(e) => setModalData(prev => ({ ...prev, serialNumber: e.target.value }))}
                placeholder="시리얼 번호를 입력하세요"
                isRequired
                isReadOnly
              />
            </div>
            <div>
              <Autocomplete
                label="그룹"
                placeholder="그룹을 검색하고 선택하거나 새 그룹명을 입력하세요"
                inputValue={groupSearchTerm}
                onInputChange={(value) => setGroupSearchTerm(value)}
                selectedKey={modalData.groups[0] || null}
                onSelectionChange={(key) => {
                  if (key) {
                    setModalData(prev => ({ ...prev, groups: [key as string] }));
                  } else {
                    setModalData(prev => ({ ...prev, groups: [] }));
                  }
                }}
                allowsCustomValue
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && groupSearchTerm.trim()) {
                    e.preventDefault();
                    setModalData(prev => ({ ...prev, groups: [groupSearchTerm.trim()] }));
                    setGroupSearchTerm('');
                  }
                }}
                className="max-w-full"
              >
                {availableGroups.map((group: any) => (
                  <AutocompleteItem key={group.name}>
                    {group.name}
                  </AutocompleteItem>
                ))}
              </Autocomplete>
            </div>
            <div>
              <Textarea
                label="설명"
                value={modalData.description}
                onChange={(e) => setModalData(prev => ({ ...prev, description: e.target.value }))}
                minRows={3}
              />
            </div>
          </form>
          <div className="flex justify-end space-x-3 mt-6">
            <Button type="button" variant="outline" onClick={() => {
              setShowEditModal(false);
              setGroupSearchTerm('');
            }}>
              취소
            </Button>
            <Button type="button" onClick={handleModalSubmit}>
              수정
            </Button>
          </div>
        </div>
      </Modal>

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={showDeleteModal && !!selectedPhoneboard}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="폰보드 삭제"
        message={selectedPhoneboard ? `정말로 ${selectedPhoneboard.name} 폰보드를 삭제하시겠습니까? 연결된 모든 디바이스 정보도 함께 삭제됩니다.` : ''}
        confirmText="삭제"
        cancelText="취소"
        confirmVariant="danger"
      />
    </div>
  );
};